import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Columns3 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

/**
 * Thứ tự cột trên bảng — danh sách này LÀ thứ tự, không chỉ là tập hợp.
 *
 * Đoạn `unit → location → cost → sellingPrice → wireSize` do khách chốt
 * 14/09/2026. `weight` và `supplier` trước đây chen giữa `unit` và `cost`, nay
 * lùi xuống ngay sau `wireSize` để giữ nguyên cụm cột khách yêu cầu. `material` (Roary 09/10/2026) ban đầu đứng
 * sau `weight` và tắt sẵn, nên Roary tưởng chưa có và xin "thêm cột Material type to thickness". Anh Toàn chốt
 * 28/10/2026: bật sẵn trong mọi bộ cột (người không cần vẫn bỏ tick được) và đứng ngay TRƯỚC cụm trên
 * (sau `colorSize`). Bản 1.1.78 từng chen nó vào giữa `location` và `cost`; anh Toàn đổi lại cùng ngày để
 * Unit / Default bin nằm sát Cost price.
 *
 * `location` tách khỏi `storage` thành cột riêng. Trước đây hai trường dùng
 * chung một ô hai dòng nhãn "Kho / Vị trí", nhưng trên dữ liệu thật 620/1.461
 * sản phẩm có Location còn Storage chỉ có ĐÚNG MỘT — và giá trị đó là "45.00",
 * một con số lọt nhầm ô. Nói cách khác cái nhãn hứa hai thứ mà dòng trên trống
 * với 1.460/1.461 dòng.
 */
export const PRODUCT_COLUMN_KEYS = ['description', 'customerRef', 'typeBrand', 'colorSize', 'material', 'unit', 'location', 'cost', 'sellingPrice', 'wireSize', 'weight', 'supplier', 'lowestPrice', 'dtdPrice', 'exwPrice', 'underMoqPrice', 'upCharge', 'leadTime', 'orderQty', 'stock', 'storage', 'currency', 'approval', 'created'] as const;
export type ProductColumnKey = typeof PRODUCT_COLUMN_KEYS[number];
export const PRODUCT_COLUMN_PRESETS = {
  everyday: ['description', 'typeBrand', 'colorSize', 'material', 'unit', 'location', 'sellingPrice', 'wireSize', 'stock', 'currency', 'approval'],
  pricing: ['description', 'customerRef', 'material', 'cost', 'sellingPrice', 'supplier', 'lowestPrice', 'dtdPrice', 'exwPrice', 'underMoqPrice', 'upCharge', 'orderQty', 'currency'],
  warehouse: ['description', 'colorSize', 'material', 'unit', 'location', 'wireSize', 'weight', 'stock', 'storage', 'orderQty'],
  all: [...PRODUCT_COLUMN_KEYS],
} satisfies Record<string, ProductColumnKey[]>;
const STORAGE_KEY = 'hisense.products.columns.v3';
/** Khoá cũ, mới nhất trước. Lựa chọn lưu ở đây có trước lúc Vật liệu bật sẵn. */
const OLDER_KEYS = ['hisense.products.columns.v2', 'hisense.products.columns.v1'];

/**
 * Lựa chọn cột đã lưu từ bản trước.
 *
 * Ai từng bật cột gộp "Kho / Vị trí" là muốn nhìn thấy vị trí hàng, nên khi
 * tách đôi thì họ phải được giữ cả hai — đổi khoá lưu mà không chuyển tiếp thì
 * mọi người mất hết cột đã chọn và tưởng hệ thống tự ý đổi.
 *
 * `beforeMaterialDefault`: lựa chọn lưu trước 28/10/2026 chưa từng được hỏi về
 * cột Vật liệu (lúc đó tắt sẵn), nên bật nó một lần. Lựa chọn lưu sau đó thì
 * tôn trọng — kể cả khi người dùng đã bỏ tick Vật liệu.
 */
export function migrateProductColumns(saved: readonly unknown[], beforeMaterialDefault = false): ProductColumnKey[] {
  const keys = new Set(saved.filter((x): x is string => typeof x === 'string'));
  if (keys.has('storage')) keys.add('location');
  if (keys.has('dodPrice')) keys.add('dtdPrice');
  if (beforeMaterialDefault) keys.add('material');
  return PRODUCT_COLUMN_KEYS.filter(key => keys.has(key));
}

/** `canSeeRef`: mã hàng của khách (YC01-L1) — người không được thấy không có cột này. */
export function useProductColumns(canSeeCost: boolean, canSeeRef: boolean) {
  const [columns, setColumns] = useState<ProductColumnKey[]>(() => {
    for (const key of [STORAGE_KEY, ...OLDER_KEYS]) {
      try {
        const saved: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
        if (Array.isArray(saved)) return migrateProductColumns(saved, key !== STORAGE_KEY);
      } catch { /* Storage may be unavailable or corrupt; try the next key. */ }
    }
    return PRODUCT_COLUMN_PRESETS.everyday;
  });
  const allowed = PRODUCT_COLUMN_KEYS.filter(key => (canSeeCost || (key !== 'cost' && key !== 'supplier')) && (canSeeRef || key !== 'customerRef'));
  const update = (keys: ProductColumnKey[]) => {
    /*
     * Chuẩn hoá về thứ tự bảng NGAY, đừng giữ thứ tự người dùng tick.
     *
     * Trước đây bật thêm một cột thì nó nhảy xuống cuối bảng, tải lại trang mới
     * về đúng chỗ — vì lúc đọc lại từ bộ nhớ mới có bước sắp xếp. Người dùng
     * đổi cột xong thấy sai thứ tự và tưởng hệ thống hỏng.
     */
    const ordered = PRODUCT_COLUMN_KEYS.filter(key => keys.includes(key));
    setColumns(ordered);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ordered)); } catch { /* Preferences remain available for this session. */ }
  };
  // Lấy theo `allowed` (đã đúng thứ tự bảng) để bộ cột mặc định cũng ra đúng vị trí, không phụ thuộc thứ tự viết trong preset.
  return { columns: allowed.filter(key => columns.includes(key)), allowed, update };
}
export function ProductColumnPicker({ columns, allowed, update }: ReturnType<typeof useProductColumns>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return <><Button variant="secondary" size="sm" onClick={() => setOpen(true)}><Columns3 className="size-4" />{t('productList.columns')}</Button>
    {open && <Modal title={t('productList.columns')} closeLabel={t('common.close')} onClose={() => setOpen(false)} className="max-w-xl">
      <div className="space-y-4 overflow-y-auto p-5">
        <p className="text-xs text-ink-soft">{t('productList.columnsNote')}</p>
        <div className="flex flex-wrap gap-2">{(Object.keys(PRODUCT_COLUMN_PRESETS) as (keyof typeof PRODUCT_COLUMN_PRESETS)[]).map(preset => <Button key={preset} variant="secondary" size="sm" onClick={() => update(PRODUCT_COLUMN_PRESETS[preset].filter(key => allowed.includes(key)))}>{t(`productList.preset_${preset}`)}</Button>)}</div>
        <div className="grid grid-cols-2 gap-3">{allowed.map(key => <label key={key} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={columns.includes(key)} onChange={event => update(event.target.checked ? [...columns, key] : columns.filter(k => k !== key))} />{t(`productList.column_${key}`)}</label>)}</div>
        <div className="flex justify-end"><Button onClick={() => setOpen(false)}>{t('common.close')}</Button></div>
      </div>
    </Modal>}
  </>;
}
